<script setup lang="ts">

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import ItemV3MostSelected from "@/components/itemV3/parameter/itemV3MostSelected.vue";
import {computed, inject, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import {getCurrentText} from "@/assets/ts/itemText";

const {t} = useI18n();

const emit = defineEmits(["patch"]);
const props = defineProps(["item"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const imageWidthPercent: number = 30
const loading = ref(true)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const boxIdx = "ratio"

const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = "discs.ratio"

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

const currentDiscsRatioText = computed(() => {
  return getCurrentText(configureAll.value ? -1 : itemIdx.value, variantIdx)
})

const open = ref(false)

const variants = computed(() => open.value ? parametersVariations.getVariants() : [])

/**
 * color variants
 */

const activeIdx = ref(0)

const variantsList = computed(() => {
  if (!variants.value.length || variants.value.length < activeIdx.value) return []
  return variants.value[activeIdx.value].values
})

const active = computed(() => {
  if (!variants.value.length || variants.value.length < activeIdx.value) return []
  return variants.value[activeIdx.value]
})

const matchedIdx = computed(() => {
  if (!variants.value.length || variants.value.length < activeIdx.value) return 0
  return variants.value[activeIdx.value].values.findIndex((v: any) => v.match)
})

/**
 * image
 */

const hoverIdx = ref(-1)

const setHoverIdx = () => {
  if (matchedIdx.value !== -1 && variantsList.value.length >= matchedIdx.value) {
    hoverIdx.value = matchedIdx.value
  }
}

watch(active, () => {
  setHoverIdx()
})

watch(props.item, () => {
  setHoverIdx()
})

const imageUrl = computed(() => parametersVariations.imageUrl(variantsList.value, hoverIdx.value))

const getMainImageUrl = (image: string) => {
  if (!image) return ''
  return image
}

const checkImageLoaded = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(true);
    img.src = url;
  });
};

const checkAllLoaded = async () => {
  const profiles = variants.value;

  if (!profiles.length) return;
  const allListsCreated = profiles.every((profile: any) => profile.values && profile.values.length > 0);
  if (!allListsCreated) return;

  loading.value = false;
};

watch(variants, () => {
  checkAllLoaded();
}, {immediate: true, deep: true});

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * tip texts
 */

const getTipTitle = computed(() => {
  if (active.value && active.value.values[hoverIdx.value]) {
    const key = hoverIdx.value == -1 ? 'free' : active.value.values[hoverIdx.value].idx || 'free'
    return t('components.parameters.tips.discsRatio.' + key + '.title')
  } else {
    return t('components.parameters.tips.discsRatio.free.title')
  }
})

const getTipText = computed(() => {
  if (active.value && active.value.values[hoverIdx.value]) {
    const key = hoverIdx.value == -1 ? 'free' : active.value.values[hoverIdx.value].idx || 'free'
    return t('components.parameters.tips.discsRatio.' + key + '.text')
  } else {
    return t('components.parameters.tips.discsRatio.free.text')
  }
})

const getTipImageUrl = computed(() => {
  if (active.value && active.value.values[hoverIdx.value]) {
    const key = active.value.values[hoverIdx.value].idx.replaceAll(":", "-")
    return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/ratio/300/emotion-' + key + '.webp'

  } else {
    return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/ratio/300/scheiben-free-emotional.webp'
  }
})

onMounted(
    () => {
      setHoverIdx()
    }
)

/*
 * focus values
 */

const focusValueIdx = [
  // idx must be a string with one decimal digit
  "1",
  "1:1",
  "2:1",
  "3:1",
  "1:1:1"
]

const getFocusValueIdx = computed(
    () => {
      if (matchedIdx.value && matchedIdx.value != -1) {
        if (!focusValueIdx.includes(variants.value[activeIdx.value].values[matchedIdx.value].idx)) {
          return [
            variants.value[activeIdx.value].values[matchedIdx.value].idx,
            ...focusValueIdx.slice(0, -1)
          ]
        }
      }

      return focusValueIdx
    }
)

</script>

<template>
  <item-v3-parameter-box
      v-model:open="open"
      :image-width-percent=imageWidthPercent
      :image=imageUrl
      :header-image="getHeaderImage()"
      :header-image-srcset="getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w'"
      title="Aufteilungen"
      :subtitle="currentDiscsRatioText || 'Wählen Sie die Scheibenaufteilung für Ihre Trauringe'"
      :loading=loading
      :tip-title="getTipTitle"
      :tip-text="getTipText"
      :tip-image="getTipImageUrl"
  >
    <template v-slot:main>

      <!-- most selected -->
      <item-v3-most-selected/>
      <ul class="flex gap-x-1 flex-wrap mb-2 w-full justify-center ">
        <!-- disc list -->
        <template v-for="(variantData, variantIdx) in variantsList">
          <li :key="variantIdx"
              v-if="getFocusValueIdx.includes(variantData.idx)"
              class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
              @click="handleValuePatch(active.idx, variantData)"
              @mouseover="hoverIdx = variantIdx as number"
              @mouseleave="hoverIdx = matchedIdx"
              :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
          >
            <div
                class="h-12 w-12 rounded-2xl flex overflow-hidden relative">
              <img
                  :src="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/ratio/100x300/scheiben-${variantData.idx.replaceAll(':', '-')}-hochkant.webp`"
                  :srcset="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/ratio/200x600/scheiben-${variantData.idx.replaceAll(':', '-')}-hochkant.webp`"
                  class="flex h-36 w-auto -top-8 object-top object-cover relative"
              >

            </div>
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
            {{ variantData.title }}
          </span>
          </li>
        </template>
      </ul>
    </template>
    <template v-slot:more>

      <ul class="flex gap-x-1 flex-wrap mb-2 w-full justify-center ">
        <!-- disc list -->
        <template v-for="(variantData, variantIdx) in variantsList">
          <li :key="variantIdx"
              v-if="!getFocusValueIdx.includes(variantData.idx)"
              class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
              @click="handleValuePatch(active.idx, variantData)"
              @mouseover="hoverIdx = variantIdx as number"
              @mouseleave="hoverIdx = matchedIdx"
              :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
          >
            <div
                class="h-12 w-12 rounded-2xl flex overflow-hidden relative">
              <img
                  :src="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/ratio/100x300/scheiben-${variantData.idx.replaceAll(':', '-')}-hochkant.webp`"
                  :srcset="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/ratio/200x600/scheiben-${variantData.idx.replaceAll(':', '-')}-hochkant.webp`"
                  class="flex h-36 w-auto -top-8 object-top object-cover relative"
              >
            </div>
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
            {{ variantData.title }}
          </span>
          </li>
        </template>
      </ul>
    </template>
  </item-v3-parameter-box>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>